import {
  Box,
  Chip,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import { DateCalendar, DateView, PickersDay } from "@mui/x-date-pickers";
import InsertInvitationOutlinedIcon from "@mui/icons-material/InsertInvitationOutlined";
import { dateFilter } from "../../utils/Constant";

interface HeaderProps {
  field: string;
  sortField: string;
  headerName: string | undefined;
  showFilter: boolean;
  width?: number;
  onFilterSubmit: (value: any) => void;
}

export default function AdvancedDatePicker({
  field,
  sortField,
  headerName,
  showFilter,
  width,
  onFilterSubmit,
}: HeaderProps) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState<any>(null);
  const [end, setEnd] = useState<any>(null);
  const [filter, setFilter] = useState("On");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [value, setValue] = useState<Dayjs | null>(null);
  const [sort, setSort] = useState("asc");
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [view, setView] = useState<DateView>("day");

  const toggleSorting = () => {
    onFilterSubmit({ sortColumn: sortField, sortOrder: sort });
    setSort(sort === "asc" ? "desc" : "asc");
  };

  const handleOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (open && selectedFilter) {
      setFilter(selectedFilter);
      if(selectedFilter === "Between"){
        const dateArray = selectedValue?.split("-");
        if(dateArray && dateArray.length > 0){
          const startDate = dayjs(dateArray[0].trim());
          const endDate = dayjs(dateArray[1].trim());
          setStart(startDate);
          setEnd(endDate);
        }
      }else {
        setValue(dayjs(selectedValue));
      }
    }
  }, [open]); // eslint-disable-line

  const handleDateChange = (date: any) => {
    setValue(date);
    setSelectedFilter(filter);
    setSelectedValue(dayjs(date).format("MM/DD/YYYY"));
    setOpen(false);
    const formattedDate = dayjs(date).format("YYYY-MM-DD");
    if (dayjs(formattedDate).isValid())
      onFilterSubmit({
        [`${field}.Operator`]: filter,
        [`${field}.Value`]: formattedDate,
        [`${field}.EndValue`]: "",
      });
    else
      onFilterSubmit({ [`${field}.Operator`]: filter, [`${field}.Value`]: "", [`${field}.EndValue`]: "" });
  };

  useEffect(() => {
    if (start && end && !open) {
      setSelectedFilter(filter);
      const startDate = dayjs(start).format("YYYY-MM-DD");
      const endDate = dayjs(end).format("YYYY-MM-DD");
      setSelectedValue(
        `${dayjs(start).format("MM/DD/YYYY")} - ${dayjs(end).format(
          "MM/DD/YYYY"
        )}`
      );
      onFilterSubmit({
        [`${field}.Operator`]: filter,
        [`${field}.Value`]: startDate,
        [`${field}.EndValue`]: endDate,
      });
    }
  }, [start, end]); // eslint-disable-line

  const handleSelect = (date: any) => {
    if (!start || (start && end)) {
      setStart(date);
      setEnd(null);
      return;
    }

    if (dayjs(date).isBefore(start, "day")) {
      setEnd(start);
      setStart(date);
    } else {
      setEnd(date);
    }

    setOpen(false);
  };

  function CustomDay(props: any) {
    const { day, ...other } = props;

    const inRange =
      start &&
      end &&
      dayjs(day).isAfter(start, "day") &&
      dayjs(day).isBefore(end, "day");

    const isStart = start && dayjs(day).isSame(start, "day");
    const isEnd = end && dayjs(day).isSame(end, "day");

    return (
      <PickersDay
        {...other}
        day={day}
        onClick={() => handleSelect(day)}
        sx={{
          ...(inRange ? { backgroundColor: "#ffdabf" } : {}),
          ...(isStart || isEnd
            ? {
                backgroundColor: "#E35F00",
                color: "#fff",
              }
            : {}),
        }}
      />
    );
  }

  return (
    <Box sx={{ width, pr: 1 }}>
      <Box className="header" sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ fontWeight: "bold" }}>{headerName}</Typography>
        <IconButton aria-label="sort" onClick={toggleSorting}>
          {sort === "asc" ? (
            <ArrowUpwardOutlinedIcon
              className="ArrowUpwardIcon"
              sx={{ fontSize: 17, display: "none" }}
            />
          ) : (
            <ArrowDownwardOutlinedIcon
              className="ArrowUpwardIcon"
              sx={{ fontSize: 17, display: "none" }}
            />
          )}
        </IconButton>
      </Box>
      <Box
        sx={{
          width: "auto",
          height: "auto",
          display: showFilter ? "block" : "none",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TextField
            sx={{ mt: 1 }}
            size="small"
            onClick={handleOpen}
            placeholder="MM/DD/YYYY"
            value={selectedValue || ""}
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: -1.5 }}>
                    <IconButton onClick={handleOpen}>
                      <InsertInvitationOutlinedIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-end"
            modifiers={[
              {
                name: "flip",
                enabled: false,
              },
              {
                name: "preventOverflow",
                enabled: false,
              },
            ]}
          >
            <ClickAwayListener onClickAway={handleClose}>
              <Paper sx={{ p: 2, display: "flex" }}>
                <Box sx={{ width: 100, pr: 2 }}>
                  {dateFilter.map((date: any, index: number) => (
                    <Box pt={index === 0 ? 0 : 1}>
                      <Chip
                        label={date.label}
                        color={filter === date.value ? "primary" : "default"}
                        onClick={() => {
                          setFilter(date.value);
                          setValue(null);
                          setStart(null);
                          setEnd(null);
                        }}
                      />
                    </Box>
                  ))}
                </Box>
                <Box>
                  {filter === "Between" && (
                    <Typography sx={{ fontWeight: "600", mb: 0.5 }}>
                      {start ? `${dayjs(start).format("MM/DD/YYYY")}` : "Start"}{" "}
                      - {end ? `${dayjs(end).format("MM/DD/YYYY")}` : "End"}
                    </Typography>
                  )}
                  <Box sx={{ border: "1px solid #C4C4C4", borderRadius: 0.5 }}>
                    {filter === "Between" ? (
                      <DateCalendar
                        slots={{ day: CustomDay }}
                        value={end}
                        shouldDisableDate={(day) =>
                          !!start && !end && dayjs(day).isBefore(start, "day")
                        }
                      />
                    ) : (
                      <DateCalendar
                        key={value ? value.format("YYYY-MM-DD") : "today"}
                        value={value}
                        views={["year", "month", "day"]}
                        view={view}
                        onViewChange={setView}
                        onChange={(newValue) => {
                          if (view !== "day") return;

                          handleDateChange(newValue);
                        }}
                        referenceDate={dayjs()}
                      />
                    )}
                  </Box>
                </Box>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </LocalizationProvider>
      </Box>
    </Box>
  );
}
